/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  ButtonLabelLayoutStyles,
  ButtonLabelLayoutVariantOptions
} from '../../UNSAFE_ButtonLabelLayout/themes/ButtonLabelLayoutStyles.css';
import { ButtonLabelLayoutRedwoodTheme } from '../../UNSAFE_ButtonLabelLayout/themes/redwood/ButtonLabelLayoutTheme';
import { ComponentProps, ComponentChildren } from 'preact';
import { ButtonLabelLayout, LayoutSizes } from '../../UNSAFE_ButtonLabelLayout/ButtonLabelLayout';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';

export type LayoutInfo = {
  startIconClasses: string;
  endIconClasses: string;
  labelClasses: string;
  labelStyles: Record<string, string>;
  suffixClasses: string;
  containerClasses: string;
  isShowIcons: boolean;
  isShowText: boolean;
  isOnlyIcon: boolean;
  hasTextStartIcon: boolean;
  hasTextEndIcon: boolean;
  isTwoIcons: boolean;
  isTwoIconsOnly: boolean;
};

export type ButtonDisplay = 'icons' | 'label' | 'all';

export type ButtonLabelLayoutProps = {
  /**
   *The default slot is the button's text label. The oj-c-button element accepts plain text or DOM nodes as children for the default slot."
   */
  children?: ComponentChildren;

  /**
   * Display indicates whether the label, the icons, or all elements should be rendered by the button.
   */
  display?: ButtonDisplay;

  /**
   * The startIcon is the button's start icon.
   */
  startIcon?: ComponentChildren;

  /**
   * The endIcon is the button's end icon.
   */
  endIcon?: ComponentChildren;

  /**
   * Styling properties
   */
  styling?: ComponentProps<typeof ButtonLabelLayout>['styling'];

  /**
   * The size indicates how large the button is rendered.
   */
  size?: ComponentProps<typeof ButtonLabelLayout>['size'];

  /**
   *The suffix is appended to the text label, used for menu buttons.
   */
  suffix?: string;
};

export function useButtonLabelLayoutTheme({
  display,
  styling,
  size,
  children,
  startIcon,
  endIcon,
  suffix
}: ButtonLabelLayoutProps): LayoutInfo {
  const isShowIcons = display === 'all' || display === 'icons';
  const isShowText = display === 'all' || display === 'label';
  const isOnlyIcon = (startIcon && (display === 'icons' || !children) && !endIcon) as boolean;
  const hasTextStartIcon = !isNullOrUndefined(startIcon) && display === 'all';
  const hasTextEndIcon = !isNullOrUndefined(endIcon) && isShowIcons;
  const isTwoIcons = !isNullOrUndefined(startIcon) && !isNullOrUndefined(endIcon);
  const isTwoIconsOnly = isTwoIcons && display === 'icons';

  const { classes: startIconClasses } = useComponentTheme<
    ButtonLabelLayoutVariantOptions,
    ButtonLabelLayoutStyles
  >(ButtonLabelLayoutRedwoodTheme, {
    target: 'startIcon',
    size: styling == 'default' ? size : (('embedded' + size) as LayoutSizes),
    onlyIcon: isOnlyIcon ? 'isOnlyIcon' : 'notOnlyIcon',
    twoIconsOnly: isTwoIconsOnly ? 'isTwoIconsOnly' : 'notTwoIconsOnly'
  });

  const { classes: labelClasses, styles: labelStyles } = useComponentTheme<
    ButtonLabelLayoutVariantOptions,
    ButtonLabelLayoutStyles
  >(ButtonLabelLayoutRedwoodTheme, {
    target: 'label',
    size: styling == 'embedded' ? (('embedded' + size) as LayoutSizes) : size,
    fill: styling == 'fill' ? 'isFill' : 'notFill',
    textEndIcon: hasTextEndIcon ? 'hasTextEndIcon' : 'notTextEndIcon',
    textStartIcon: hasTextStartIcon ? 'hasTextStartIcon' : 'notTextStartIcon',
    suffix: suffix ? 'hasSuffix' : 'notSuffix',
    twoIcons: isTwoIcons ? 'hasTwoIcons' : 'notTwoIcons',
    loading: 'notLoading'
  });

  const { classes: suffixClasses } = useComponentTheme<
    ButtonLabelLayoutVariantOptions,
    ButtonLabelLayoutStyles
  >(ButtonLabelLayoutRedwoodTheme, {
    target: 'suffix',
    size: styling == 'default' ? size : (('embedded' + size) as LayoutSizes),
    textStartIcon: hasTextStartIcon ? 'hasTextStartIcon' : 'notTextStartIcon',
    twoIcons: isTwoIcons ? 'hasTwoIcons' : 'notTwoIcons'
  });

  const { classes: endIconClasses } = useComponentTheme<
    ButtonLabelLayoutVariantOptions,
    ButtonLabelLayoutStyles
  >(ButtonLabelLayoutRedwoodTheme, {
    target: 'endIcon',
    textEndIcon: hasTextEndIcon ? 'hasTextEndIcon' : 'notTextEndIcon',
    twoIcons: isTwoIcons ? 'hasTwoIcons' : 'notTwoIcons',
    size: styling == 'default' ? size : (('embedded' + size) as LayoutSizes)
  });

  return {
    startIconClasses,
    endIconClasses,
    labelClasses,
    labelStyles,
    suffixClasses,
    containerClasses: labelStyles.container,
    isShowIcons,
    isShowText,
    isOnlyIcon,
    hasTextStartIcon,
    hasTextEndIcon,
    isTwoIcons,
    isTwoIconsOnly
  };
}

const isNullOrUndefined = (
  value: ComponentChildren | null | undefined
): value is null | undefined => {
  return value === null || value === undefined;
};
